/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import React from "react";
import { Link, Redirect } from "react-router-dom";

import { isEventDashboardPermissionError } from "../eventDashboardPermissionError";
import { useGetDashboardEventRoles } from "./useGetDashboardEventRoles";
import { useGetDashboardEvents } from "./useGetDashboardEvents";

const formatEventDateTime = (eventDateSeconds: number): string =>
  format(new Date(eventDateSeconds * 1000), "yyyy/MM/dd(E) HH:mm", {
    locale: ja,
  });

const createSalesDashboardPath = (eventId: string): string => {
  const searchParams = new URLSearchParams({ event_id: eventId });

  return `/admin/sales?${searchParams.toString()}`;
};

export const AdminDashboard: React.FC = () => {
  const {
    eventRoles,
    isLoading: isLoadingEventRoles,
    isValidating: isValidatingEventRoles,
    error: eventRolesError,
  } = useGetDashboardEventRoles();
  const salesDashboardEventIds = React.useMemo(
    () =>
      eventRoles.flatMap(({ eventId, roles }) => {
        return roles.includes("sales_dashboard_viewer") ? [eventId] : [];
      }),
    [eventRoles]
  );
  const {
    events,
    isLoading: isLoadingEvents,
    isValidating: isValidatingEvents,
    error: eventsError,
  } = useGetDashboardEvents(salesDashboardEventIds);
  const dashboardEvents = React.useMemo(
    () =>
      [...events].sort(
        (previousEvent, currentEvent) =>
          currentEvent.eventDateSeconds - previousEvent.eventDateSeconds
      ),
    [events]
  );

  if (isEventDashboardPermissionError(eventRolesError)) {
    return <Redirect to="/" />;
  }

  return (
    <main css={styles.page}>
      <section
        aria-busy={isValidatingEventRoles || isValidatingEvents}
        css={styles.container}
      >
        <header css={styles.pageHeader}>
          <h1 css={styles.title}>イベント一覧</h1>
          {dashboardEvents.length > 0 ? (
            <p css={styles.displayedEventCount}>
              {dashboardEvents.length}件を表示
            </p>
          ) : null}
        </header>

        {(eventRolesError ?? eventsError) ? (
          <p css={styles.statusMessage}>イベント一覧の取得に失敗しました。</p>
        ) : isLoadingEventRoles || isLoadingEvents ? (
          <p css={styles.statusMessage}>読み込み中...</p>
        ) : dashboardEvents.length === 0 ? (
          <p css={styles.statusMessage}>表示できるイベントはありません。</p>
        ) : (
          <div css={styles.eventTable}>
            <div aria-hidden="true" css={styles.eventTableHeader}>
              <span />
              <span>イベント</span>
              <span>開催日時</span>
              <span />
            </div>
            <ul css={styles.eventList}>
              {dashboardEvents.map(
                ({ eventDateSeconds, eventId, eventImageUrl, eventName }) => (
                  <li key={eventId} css={styles.eventListItem}>
                    <Link
                      css={styles.eventLink}
                      to={createSalesDashboardPath(eventId)}
                    >
                      <span css={styles.eventImage}>
                        {eventImageUrl ? (
                          <img
                            alt=""
                            css={styles.eventImageElement}
                            loading="lazy"
                            src={eventImageUrl}
                          />
                        ) : (
                          <ImageOutlinedIcon
                            aria-hidden="true"
                            css={styles.eventImagePlaceholder}
                          />
                        )}
                      </span>
                      <span css={styles.eventName}>{eventName}</span>
                      <time
                        css={styles.eventDate}
                        dateTime={new Date(
                          eventDateSeconds * 1000
                        ).toISOString()}
                      >
                        <span css={styles.eventDateLabel}>開催日時</span>
                        <span>{formatEventDateTime(eventDateSeconds)}</span>
                      </time>
                      <ChevronRightIcon
                        aria-hidden="true"
                        css={styles.eventChevron}
                      />
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>
        )}
      </section>
    </main>
  );
};

const styles = {
  page: css`
    width: 100%;
    min-height: 100vh;
    padding: clamp(24px, 3.5vw, 40px) clamp(16px, 4vw, 44px) 64px;
    color: #ffffff;
    background: #131c26;
    @media (width < 48rem) {
      padding: 22px 16px 48px;
    }
  `,
  container: css`
    box-sizing: border-box;
    width: 100%;
    max-width: 1360px;
    margin: 0 auto;
  `,
  pageHeader: css`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 20px;
    @media (width < 48rem) {
      align-items: center;
      gap: 16px;
      margin-bottom: 16px;
    }
  `,
  title: css`
    margin: 0;
    color: #ffffff;
    font-size: clamp(22px, 3vw, 28px);
    font-weight: 800;
    letter-spacing: 0.4px;
    line-height: 1.25;
    @media (width < 48rem) {
      font-size: 20px;
    }
  `,
  displayedEventCount: css`
    margin: 0;
    color: #7e8b98;
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
    @media (width < 48rem) {
      font-size: 11px;
    }
  `,
  statusMessage: css`
    padding: 40px 24px;
    margin: 0;
    color: #c4ced7;
    font-size: 14px;
    text-align: center;
    line-height: 1.5;
    background: #1b2734;
    border: 1px solid #2a3948;
    border-radius: 6px;
  `,
  eventTable: css`
    background: #1b2734;
    border: 1px solid #2a3948;
    border-radius: 6px;
    overflow: hidden;
    @media (width < 48rem) {
      background: transparent;
      border: 0;
      border-radius: 0;
      overflow: visible;
    }
  `,
  eventTableHeader: css`
    display: grid;
    grid-template-columns: 104px minmax(0, 1fr) 180px 52px;
    align-items: center;
    gap: 16px;
    min-height: 48px;
    padding: 0 12px 0 16px;
    color: #768493;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.3px;
    line-height: 1.5;
    background: #213141;
    @media (width < 48rem) {
      display: none;
    }
  `,
  eventList: css`
    padding: 0;
    margin: 0;
    list-style: none;
    @media (width < 48rem) {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
  `,
  eventListItem: css`
    background: #1b2734;
    border-top: 1px solid #2a3948;
    &:first-of-type {
      border-top: 0;
    }
    @media (width < 48rem) {
      border: 1px solid #2a3948;
      border-radius: 8px;
      overflow: hidden;
      &:first-of-type {
        border-top: 1px solid #2a3948;
      }
    }
  `,
  eventLink: css`
    display: grid;
    grid-template-columns: 104px minmax(0, 1fr) 180px 52px;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 10px 12px 10px 16px;
    color: inherit;
    text-decoration: none;
    transition: background-color 120ms ease;
    &:hover {
      background: #1f3040;
    }
    &:focus-visible {
      outline: 2px solid #00c2ae;
      outline-offset: -2px;
    }
    @media (width < 48rem) {
      grid-template-areas:
        "image name"
        ". date";
      grid-template-columns: 72px minmax(0, 1fr);
      align-items: start;
      column-gap: 12px;
      row-gap: 12px;
      padding: 16px;
    }
  `,
  eventImage: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 104px;
    aspect-ratio: 8 / 5;
    color: #7c8b99;
    background: #223242;
    border-radius: 6px;
    overflow: hidden;
    @media (width < 48rem) {
      grid-area: image;
      width: 72px;
      aspect-ratio: 3 / 2;
    }
  `,
  eventImageElement: css`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  eventImagePlaceholder: css`
    width: 22px;
    height: 22px;
  `,
  eventName: css`
    min-width: 0;
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.15px;
    line-height: 1.5;
    overflow-wrap: anywhere;
    @media (width < 48rem) {
      grid-area: name;
      align-self: start;
      font-size: 14px;
      line-height: 1.45;
    }
  `,
  eventDate: css`
    color: #98a5b1;
    font-size: 13px;
    letter-spacing: 0.1px;
    line-height: 1.5;
    white-space: nowrap;
    @media (width < 48rem) {
      grid-area: date;
      align-self: start;
      display: flex;
      flex-direction: column;
      gap: 1px;
      font-size: 13px;
    }
  `,
  eventDateLabel: css`
    display: none;
    @media (width < 48rem) {
      display: block;
      color: #778593;
      font-size: 11px;
      line-height: 1.5;
    }
  `,
  eventChevron: css`
    display: block;
    justify-self: center;
    width: 20px;
    height: 20px;
    color: #70808e;
    @media (width < 48rem) {
      display: none;
    }
  `,
};
